import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Member, Reservation, PassengerVehicle, RecreationalVehicle } from '../../types';
import { MemberReview, MemberRatingSummary } from '../../types/memberReview';
import { useMemberData } from '../../contexts/MemberDataContext';
import {
  deleteMember,
  deletePassengerVehicle,
  deleteRecreationalVehicle,
} from '../../services/memberService';
import {
  getReservationsByMemberId,
  unlinkReservationMember,
  ApiReservation,
} from '../../services/reservationService';
import {
  listReviewsForMember,
  getMemberRatingSummary,
} from '../../services/memberReviewService';
import { useModificationHistory } from '../../services/useModificationHistory';
import ModificationHistorySection from '../reservations/ModificationHistorySection';
import ReservationReviewSection, { StarDisplay } from '../reservations/ReservationReviewSection';
import { authService } from '../../services/auth';
import { formatDate, formatDateRange, isFuture, isPast } from '../../utils/dateUtils';
import { formatCurrency, formatStatus, formatPhone, getStatusColor, getPaymentStatusColor, getReservationReviewClass } from '../../utils/formatters';
import Modal from '../shared/Modal';
import MemberFormModal from './MemberFormModal';
import PassengerVehicleFormModal from './PassengerVehicleFormModal';
import RecreationalVehicleFormModal from './RecreationalVehicleFormModal';
import DeleteConfirmModal from './DeleteConfirmModal';
import AdminCancellationWizard from '../reservations/CancellationWizard/AdminCancellationWizard';
import ModifyReservationWizard from '../reservations/ModifyReservationWizard';
import ResendAgreementModal from '../reservations/ResendAgreementModal';
import PaymentPopup from '../reservations/PaymentPopup';
import { fetchPaymentsForReservation, PaymentRecord } from '../../services/paymentService';
import './MemberCard.css';

const MemberCard: React.FC = () => {
  const { memberId } = useParams<{ memberId: string }>();
  const navigate = useNavigate();

  // Use MemberDataContext for cached data and lazy loading
  const {
    loadMemberDetails,
    loadMemberVehicles,
    loadMemberReservations,
    getMemberDetails,
    isMemberDetailsLoading,
    invalidateMemberDetails,
    onMemberDeleted,
  } = useMemberData();

  const [member, setMember] = useState<Member | null>(null);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [passengerVehicles, setPassengerVehicles] = useState<PassengerVehicle[]>([]);
  const [recreationalVehicles, setRecreationalVehicles] = useState<RecreationalVehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);

  // Edit / Delete state
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [memberReservations, setMemberReservations] = useState<ApiReservation[]>([]);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showCancelWizard, setShowCancelWizard] = useState(false);
  const [showModifyWizard, setShowModifyWizard] = useState(false);
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [showResendAgreement, setShowResendAgreement] = useState(false);
  const [modalPayments, setModalPayments] = useState<PaymentRecord[]>([]);
  const [modalPaymentLoading, setModalPaymentLoading] = useState(false);

  // Review state
  const [memberReviews, setMemberReviews] = useState<MemberReview[]>([]);
  const [ratingSummary, setRatingSummary] = useState<MemberRatingSummary | null>(null);

  // Vehicle CRUD state
  const [showPVModal, setShowPVModal] = useState(false);
  const [editingPV, setEditingPV] = useState<PassengerVehicle | null>(null);
  const [showRVModal, setShowRVModal] = useState(false);
  const [editingRV, setEditingRV] = useState<RecreationalVehicle | null>(null);

  // Modification history (shared hook)
  const modHistory = useModificationHistory(selectedReservation, modalPayments);

  // Load member data using context (with caching)
  useEffect(() => {
    if (!memberId) return;
    const id = parseInt(memberId, 10);
    loadMemberData(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId]); // Only re-run when memberId changes, not on loadMemberData reference change

  const loadMemberData = async (id: number) => {
    setLoading(true);
    setError(null);

    try {
      // Load member details (cached if available)
      const details = await loadMemberDetails(id);
      if (!details) {
        throw new Error('Member not found');
      }

      setMember(details.member);

      // Lazy load vehicles, reservations, and reviews in parallel
      await Promise.all([
        loadMemberVehicles(id),
        loadMemberReservations(id),
      ]);

      // Get updated details after lazy loading
      const updatedDetails = getMemberDetails(id);
      if (updatedDetails) {
        setPassengerVehicles(updatedDetails.passengerVehicles);
        setRecreationalVehicles(updatedDetails.recreationalVehicles);
        setReservations(
          updatedDetails.reservations.sort(
            (a, b) => new Date(b.checkIn).getTime() - new Date(a.checkIn).getTime()
          )
        );
      }

      // Load reviews and rating summary (non-blocking — don't fail member load if this errors)
      Promise.all([
        listReviewsForMember(id),
        getMemberRatingSummary(id),
      ])
        .then(([reviews, summary]) => {
          setMemberReviews(reviews);
          setRatingSummary(summary);
        })
        .catch(err => console.error('Failed to load member reviews:', err));
    } catch (err: any) {
      console.error('Error loading member data:', err);
      setError(err.message || 'Failed to load member data');
    } finally {
      setLoading(false);
    }
  };

  // Update local state when context cache changes
  useEffect(() => {
    if (!memberId) return;
    const id = parseInt(memberId, 10);
    const details = getMemberDetails(id);
    if (details) {
      setMember(details.member);
      setPassengerVehicles(details.passengerVehicles);
      setRecreationalVehicles(details.recreationalVehicles);
      setReservations(
        details.reservations.sort(
          (a, b) => new Date(b.checkIn).getTime() - new Date(a.checkIn).getTime()
        )
      );
    }
  }, [memberId, getMemberDetails]);

  const loadModalPayments = useCallback(() => {
    if (!selectedReservation) {
      setModalPayments([]);
      return;
    }
    const storeId = authService.getStoreId();
    if (!storeId) return;
    setModalPaymentLoading(true);
    const fetches = [fetchPaymentsForReservation(selectedReservation.id, storeId)];
    if (selectedReservation.originalReservationId) {
      fetches.push(fetchPaymentsForReservation(selectedReservation.originalReservationId, storeId));
    }
    Promise.all(fetches)
      .then((results) => {
        const merged = results.flat();
        const unique = Array.from(new Map(merged.map((p) => [p.id, p])).values());
        setModalPayments(unique);
      })
      .catch((err) => console.error('Payment fetch error:', err))
      .finally(() => setModalPaymentLoading(false));
  }, [selectedReservation]);

  useEffect(() => {
    loadModalPayments();
  }, [loadModalPayments]);

  const getCorporateCode = (): string | number => {
    const userData = localStorage.getItem('managerUserData');
    if (userData) {
      const parsed = JSON.parse(userData);
      return parsed.corporate_code || parsed.corporate_id || '';
    }
    return '';
  };

  const handleDeleteClick = async () => {
    if (!member) return;
    try {
      const res = await getReservationsByMemberId(member.id);
      setMemberReservations(res);
    } catch {
      setMemberReservations([]);
    }
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!member) return;
    setDeleteLoading(true);
    try {
      for (const res of memberReservations) {
        await unlinkReservationMember(res.id);
      }
      await deleteMember(member.id);
      onMemberDeleted(member.id);
      navigate('/members');
    } catch (err: any) {
      console.error('Error deleting member:', err);
      alert(err.message || 'Failed to delete member');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Passenger Vehicle handlers
  const handleAddPV = () => {
    setEditingPV(null);
    setShowPVModal(true);
  };
  const handleEditPV = (vehicle: PassengerVehicle) => {
    setEditingPV(vehicle);
    setShowPVModal(true);
  };
  const handleDeletePV = async (vehicle: PassengerVehicle) => {
    if (!window.confirm(`Delete passenger vehicle ${vehicle.licenseNo || ''}?`)) return;
    try {
      await deletePassengerVehicle(vehicle.id);
      if (memberId) {
        invalidateMemberDetails(parseInt(memberId, 10));
        loadMemberData(parseInt(memberId, 10));
      }
    } catch (err: any) {
      alert(err.message || 'Failed to delete vehicle');
    }
  };

  // Recreational Vehicle handlers
  const handleAddRV = () => {
    setEditingRV(null);
    setShowRVModal(true);
  };
  const handleEditRV = (vehicle: RecreationalVehicle) => {
    setEditingRV(vehicle);
    setShowRVModal(true);
  };
  const handleDeleteRV = async (vehicle: RecreationalVehicle) => {
    if (!window.confirm(`Delete recreational vehicle "${vehicle.type}"?`)) return;
    try {
      await deleteRecreationalVehicle(vehicle.id);
      if (memberId) {
        invalidateMemberDetails(parseInt(memberId, 10));
        loadMemberData(parseInt(memberId, 10));
      }
    } catch (err: any) {
      alert(err.message || 'Failed to delete vehicle');
    }
  };

  const handleVehicleSaved = () => {
    if (memberId) {
      invalidateMemberDetails(parseInt(memberId, 10));
      loadMemberData(parseInt(memberId, 10));
    }
  };

  const activeReservations = useMemo(
    () => reservations.filter(r =>
      r.status !== 'cancelled' &&
      (isFuture(r.checkIn) || r.status === 'checked_in')
    ),
    [reservations]
  );
  const pastReservations = useMemo(
    () => reservations.filter(r =>
      r.status !== 'cancelled' &&
      isPast(r.checkIn) &&
      r.status !== 'checked_in'
    ),
    [reservations]
  );
  const cancelledReservations = useMemo(
    () => reservations.filter(r => r.status === 'cancelled'),
    [reservations]
  );

  // Map reservationId → review for O(1) lookup when rendering rows
  const reviewMap = useMemo(() => {
    const map = new Map<number, MemberReview>();
    memberReviews.forEach(r => {
      if (r.reservationId != null) map.set(r.reservationId, r);
    });
    return map;
  }, [memberReviews]);

  if (loading || isMemberDetailsLoading(memberId ? parseInt(memberId, 10) : 0)) {
    return (
      <div className="member-card-page">
        <div className="member-card-header">
          <Link to="/members" className="back-link">&larr; Back to Members</Link>
        </div>
        <div className="no-results"><p>Loading member data...</p></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="member-card-page">
        <div className="member-card-header">
          <Link to="/members" className="back-link">&larr; Back to Members</Link>
        </div>
        <div className="no-results">
          <p>{error}</p>
          <button
            onClick={() => memberId && loadMemberData(parseInt(memberId, 10))}
            className="btn btn-secondary"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!member) {
    return (
      <div className="member-not-found">
        <h2>Member Not Found</h2>
        <p>The requested member could not be found.</p>
        <Link to="/members" className="back-link">&larr; Back to Members</Link>
      </div>
    );
  }

  const handleEditReservation = () => {
    setShowModifyWizard(true);
  };

  const handleCancelReservation = () => {
    setShowCancelWizard(true);
  };

  const handleResendAgreement = () => {
    setShowResendAgreement(true);
  };

  const handleDeleteReservation = () => {
    alert('Delete reservation functionality coming soon.');
  };

  const handleWizardSuccess = () => {
    setSelectedReservation(null);
    setShowCancelWizard(false);
    setShowModifyWizard(false);
    if (memberId) {
      invalidateMemberDetails(parseInt(memberId, 10));
      loadMemberData(parseInt(memberId, 10));
    }
  };

  const renderReservationModal = () => {
    if (!selectedReservation) return null;

    const canModify = isFuture(selectedReservation.checkIn) || selectedReservation.status === 'checked_in';

    const footer = (
      <>
        <button className="btn btn-secondary" onClick={() => setSelectedReservation(null)}>
          Close
        </button>
        <button className="btn btn-primary" onClick={handleResendAgreement}>
          REISSUE CONFIRMATION
        </button>
        {canModify && (
          <>
            <button className="btn btn-warning" onClick={handleEditReservation}>
              EDIT
            </button>
            <button className="btn btn-danger" onClick={handleCancelReservation}>
              CANCELLATION
            </button>
            <button className="btn btn-danger" onClick={handleDeleteReservation}>
              DELETE
            </button>
          </>
        )}
      </>
    );

    return (
      <Modal
        isOpen={true}
        onClose={() => setSelectedReservation(null)}
        title={`Reservation ${selectedReservation.confirmationCode}`}
        size="medium"
        footer={footer}
      >
        <div className="reservation-detail">
          <div className="detail-status">
            <span
              className="status-badge"
              style={{ backgroundColor: getStatusColor(selectedReservation.status) }}
            >
              {formatStatus(selectedReservation.status)}
            </span>
          </div>

          <div className="detail-grid">
            <div className="detail-item">
              <label>Dates</label>
              <span>{formatDateRange(selectedReservation.checkIn + 'T12:00:00', selectedReservation.checkOut + 'T12:00:00')}</span>
            </div>
            <div className="detail-item">
              <label>Site</label>
              <span>{selectedReservation.siteAlias}</span>
            </div>
            <div className="detail-item">
              <label>Nights</label>
              <span>{selectedReservation.days}</span>
            </div>
            <div className="detail-item">
              <label>Guests</label>
              <span>{selectedReservation.adults} adults, {selectedReservation.children} children</span>
            </div>
            <div className="detail-item">
              <label>Pets</label>
              <span>{selectedReservation.pets}</span>
            </div>
            <div className="detail-item">
              <label>Vehicles</label>
              <span>{selectedReservation.vehicles}</span>
            </div>
            <div className="detail-item">
              <label>Total Cost</label>
              <span>{formatCurrency(selectedReservation.totalCost)}</span>
            </div>
            <div className="detail-item">
              <label>Payment Status</label>
              {modalPaymentLoading ? (
                <span style={{ color: 'var(--text-secondary)' }}>Loading...</span>
              ) : modalPayments.length > 0 ? (
                <span style={{ color: getPaymentStatusColor(modalPayments[0].status) }}>
                  {formatStatus(modalPayments[0].status)}
                </span>
              ) : (
                <span style={{ color: 'var(--text-secondary)' }}>No Payments</span>
              )}
            </div>
          </div>

          <div className="detail-item">
            <span
              className="view-payment-link"
              onClick={() => setShowPaymentPopup(true)}
            >
              View Payment Details &rarr;
            </span>
          </div>

          {selectedReservation.specialRequests && (
            <div className="detail-requests">
              <label>Special Requests</label>
              <p>{selectedReservation.specialRequests}</p>
            </div>
          )}

          {/* Modification History */}
          <ModificationHistorySection
            {...modHistory}
            reservation={selectedReservation ?? undefined}
            onPaymentsChanged={loadModalPayments}
          />

          {/* Member Review */}
          {selectedReservation.memberId && (
            <ReservationReviewSection
              reservationId={selectedReservation.id}
              memberId={selectedReservation.memberId}
              onReviewSaved={() => {
                const id = memberId ? parseInt(memberId, 10) : null;
                if (id) {
                  Promise.all([listReviewsForMember(id), getMemberRatingSummary(id)])
                    .then(([reviews, summary]) => {
                      setMemberReviews(reviews);
                      setRatingSummary(summary);
                    })
                    .catch(err => console.error('Failed to refresh reviews:', err));
                }
              }}
            />
          )}

          {!canModify && (
            <div className="detail-notice">
              This reservation is in the past and cannot be modified.
            </div>
          )}
        </div>
      </Modal>
    );
  };

  return (
    <div className="member-card-page">
      <div className="member-card-header">
        <Link to="/members" className="back-link">&larr; Back to Members</Link>
        <div className="card-header-row">
          <div>
            <h1 className="page-title">{member.firstName} {member.lastName}</h1>
            {ratingSummary && ratingSummary.reviewCount > 0 && (
              <div className="member-rating-summary">
                <span className="rating-summary-badge rating-summary-badge--neutral">
                  <StarDisplay rating={Math.round(ratingSummary.avgRating)} size="sm" />
                  &nbsp;{ratingSummary.avgRating.toFixed(1)} &middot; {ratingSummary.reviewCount} review{ratingSummary.reviewCount !== 1 ? 's' : ''}
                </span>
                {ratingSummary.flaggedCount > 0 && (
                  <span className="rating-summary-badge rating-summary-badge--warning">
                    &#9888; Not Welcome Back at {ratingSummary.flaggedCount} campground{ratingSummary.flaggedCount !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="card-header-actions">
            <button className="btn btn-secondary" onClick={() => setShowEditModal(true)}>
              Edit
            </button>
            <button className="btn btn-danger" onClick={handleDeleteClick}>
              Delete
            </button>
          </div>
        </div>
      </div>

      <div className="member-card-content">
        {/* Member Details Section */}
        <section className="member-details-section">
          <h2 className="section-title">Member Information</h2>
          <div className="member-details-card">
            <div className="details-grid">
              <div className="detail-item">
                <label>Username</label>
                <span>{member.username}</span>
              </div>
              <div className="detail-item">
                <label>Email</label>
                <span>{member.email}</span>
              </div>
              <div className="detail-item">
                <label>Phone</label>
                <span>{member.phone ? formatPhone(member.phone) : '-'}</span>
              </div>
              {member.secondPhone && (
                <div className="detail-item">
                  <label>Secondary Phone</label>
                  <span>{formatPhone(member.secondPhone)}</span>
                </div>
              )}
              <div className="detail-item">
                <label>Address</label>
                <span>
                  {member.address ? (
                    <>
                      {member.address}<br />
                      {member.city}, {member.state} {member.zipCode}
                      {member.country && `, ${member.country}`}
                    </>
                  ) : '-'}
                </span>
              </div>
              <div className="detail-item">
                <label>Joined</label>
                <span>{member.createdAt ? formatDate(member.createdAt) : '-'}</span>
              </div>
              <div className="detail-item">
                <label>Last Login</label>
                <span>{member.lastLoginDate ? formatDate(member.lastLoginDate) : 'Never'}</span>
              </div>
              <div className="detail-item">
                <label>Status</label>
                <span className={`status-badge status-badge--${member.banned ? 'inactive' : 'active'}`}>
                  {member.banned ? 'Banned' : 'Active'}
                </span>
              </div>
            </div>

            {/* Emergency Contact */}
            {(member.emergencyName || member.emergencyPhone) && (
              <div className="member-notes">
                <label>Emergency Contact</label>
                <p>
                  {member.emergencyName}
                  {member.emergencyRelation && ` (${member.emergencyRelation})`}
                  {member.emergencyPhone && ` — ${formatPhone(member.emergencyPhone)}`}
                </p>
              </div>
            )}

            {member.notes && (
              <div className="member-notes">
                <label>Notes</label>
                <p>{member.notes}</p>
              </div>
            )}
          </div>
        </section>

        {/* Passenger Vehicles Section */}
        <section className="member-details-section">
          <div className="section-header">
            <h2 className="section-title">Passenger Vehicles</h2>
            <button className="btn btn-primary btn-sm" onClick={handleAddPV}>
              + Add
            </button>
          </div>
          {passengerVehicles.length === 0 ? (
            <p className="no-reservations">No passenger vehicles on file.</p>
          ) : (
            <div className="member-list">
              <table className="member-table">
                <thead>
                  <tr>
                    <th>License No</th>
                    <th>Make</th>
                    <th>Model</th>
                    <th>Color</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {passengerVehicles.map(v => (
                    <tr key={v.id}>
                      <td><strong>{v.licenseNo}</strong></td>
                      <td>{v.make || '-'}</td>
                      <td>{v.model || '-'}</td>
                      <td>{v.color || '-'}</td>
                      <td>
                        <span className={`status-badge status-badge--${v.active ? 'active' : 'inactive'}`}>
                          {v.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <button className="btn btn-secondary btn-sm" onClick={() => handleEditPV(v)}>
                          Edit
                        </button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDeletePV(v)} style={{ marginLeft: '0.5rem' }}>
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Recreational Vehicles Section */}
        <section className="member-details-section">
          <div className="section-header">
            <h2 className="section-title">Recreational Vehicles</h2>
            <button className="btn btn-primary btn-sm" onClick={handleAddRV}>
              + Add
            </button>
          </div>
          {recreationalVehicles.length === 0 ? (
            <p className="no-reservations">No recreational vehicles on file.</p>
          ) : (
            <div className="member-list">
              <table className="member-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Description</th>
                    <th>License Plate</th>
                    <th>Length</th>
                    <th>Amp</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recreationalVehicles.map(v => (
                    <tr key={v.id}>
                      <td><strong>{v.type}</strong></td>
                      <td>{v.description || '-'}</td>
                      <td>{v.licensePlate || '-'}</td>
                      <td>{v.length ? `${v.length} ft` : '-'}</td>
                      <td>{v.amp ? `${v.amp}A` : '-'}</td>
                      <td>
                        <span className={`status-badge status-badge--${v.active ? 'active' : 'inactive'}`}>
                          {v.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <button className="btn btn-secondary btn-sm" onClick={() => handleEditRV(v)}>
                          Edit
                        </button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDeleteRV(v)} style={{ marginLeft: '0.5rem' }}>
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Reservation History Section */}
        <section className="reservation-history-section">
          <h2 className="section-title">Reservation History</h2>

          {reservations.length === 0 ? (
            <p className="no-reservations">No reservations found for this member.</p>
          ) : (
            <>
              {activeReservations.length > 0 && (
                <div className="reservation-group">
                  <h3 className="group-title">Active Reservations</h3>
                  <div className="reservation-list">
                    {activeReservations.map(reservation => {
                      const rev = reviewMap.get(reservation.id);
                      const reviewClass = rev ? getReservationReviewClass(rev.rating, rev.wouldWelcomeBack) : '';
                      return (
                      <div
                        key={`${reservation.id}-${reservation.siteId}`}
                        className={`reservation-item reservation-item--upcoming${reviewClass ? ' ' + reviewClass : ''}`}
                        onClick={() => setSelectedReservation(reservation)}
                      >
                        <div className="reservation-dates">
                          <span className="date-range">
                            {formatDateRange(reservation.checkIn + 'T12:00:00', reservation.checkOut + 'T12:00:00')}
                          </span>
                          <span className="nights">{reservation.days} nights</span>
                        </div>
                        <div className="reservation-site">
                          Site {reservation.siteAlias}
                        </div>
                        <div className="reservation-status">
                          <span
                            className="status-badge"
                            style={{ backgroundColor: getStatusColor(reservation.status) }}
                          >
                            {formatStatus(reservation.status)}
                          </span>
                        </div>
                        <div className="reservation-cost">
                          {formatCurrency(reservation.totalCost)}
                        </div>
                        <div className="reservation-actions">
                          {rev && (
                            <div className="reservation-review-indicator">
                              <span className="reservation-review-stars">{'★'.repeat(rev.rating)}{'☆'.repeat(5 - rev.rating)}</span>
                              {reviewClass === 'reservation-item--restricted' && (
                                <span className="reservation-review-label reservation-review-label--restricted">Restricted</span>
                              )}
                              {reviewClass === 'reservation-item--be-aware' && (
                                <span className="reservation-review-label reservation-review-label--be-aware">Be Aware</span>
                              )}
                            </div>
                          )}
                          <span className="view-details">View Details &rarr;</span>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {pastReservations.length > 0 && (
                <div className="reservation-group">
                  <h3 className="group-title">Past Reservations</h3>
                  <div className="reservation-list">
                    {pastReservations.map(reservation => {
                      const rev = reviewMap.get(reservation.id);
                      const reviewClass = rev ? getReservationReviewClass(rev.rating, rev.wouldWelcomeBack) : '';
                      return (
                      <div
                        key={`${reservation.id}-${reservation.siteId}`}
                        className={`reservation-item reservation-item--past${reviewClass ? ' ' + reviewClass : ''}`}
                        onClick={() => setSelectedReservation(reservation)}
                      >
                        <div className="reservation-dates">
                          <span className="date-range">
                            {formatDateRange(reservation.checkIn + 'T12:00:00', reservation.checkOut + 'T12:00:00')}
                          </span>
                          <span className="nights">{reservation.days} nights</span>
                        </div>
                        <div className="reservation-site">
                          Site {reservation.siteAlias}
                        </div>
                        <div className="reservation-status">
                          <span
                            className="status-badge"
                            style={{ backgroundColor: getStatusColor(reservation.status) }}
                          >
                            {formatStatus(reservation.status)}
                          </span>
                        </div>
                        <div className="reservation-cost">
                          {formatCurrency(reservation.totalCost)}
                        </div>
                        <div className="reservation-actions">
                          {rev && (
                            <div className="reservation-review-indicator">
                              <span className="reservation-review-stars">{'★'.repeat(rev.rating)}{'☆'.repeat(5 - rev.rating)}</span>
                              {reviewClass === 'reservation-item--restricted' && (
                                <span className="reservation-review-label reservation-review-label--restricted">Restricted</span>
                              )}
                              {reviewClass === 'reservation-item--be-aware' && (
                                <span className="reservation-review-label reservation-review-label--be-aware">Be Aware</span>
                              )}
                            </div>
                          )}
                          <span className="view-details">View Details &rarr;</span>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {cancelledReservations.length > 0 && (
                <div className="reservation-group">
                  <h3 className="group-title">Cancelled Reservations</h3>
                  <div className="reservation-list">
                    {cancelledReservations.map(reservation => {
                      const rev = reviewMap.get(reservation.id);
                      const reviewClass = rev ? getReservationReviewClass(rev.rating, rev.wouldWelcomeBack) : '';
                      return (
                      <div
                        key={`${reservation.id}-${reservation.siteId}`}
                        className={`reservation-item reservation-item--past${reviewClass ? ' ' + reviewClass : ''}`}
                        onClick={() => setSelectedReservation(reservation)}
                      >
                        <div className="reservation-dates">
                          <span className="date-range">
                            {formatDateRange(reservation.checkIn + 'T12:00:00', reservation.checkOut + 'T12:00:00')}
                          </span>
                          <span className="nights">{reservation.days} nights</span>
                        </div>
                        <div className="reservation-site">
                          Site {reservation.siteAlias}
                        </div>
                        <div className="reservation-status">
                          <span
                            className="status-badge"
                            style={{ backgroundColor: getStatusColor(reservation.status) }}
                          >
                            {formatStatus(reservation.status)}
                          </span>
                        </div>
                        <div className="reservation-cost">
                          {formatCurrency(reservation.totalCost)}
                        </div>
                        <div className="reservation-actions">
                          {rev && (
                            <div className="reservation-review-indicator">
                              <span className="reservation-review-stars">{'★'.repeat(rev.rating)}{'☆'.repeat(5 - rev.rating)}</span>
                              {reviewClass === 'reservation-item--restricted' && (
                                <span className="reservation-review-label reservation-review-label--restricted">Restricted</span>
                              )}
                              {reviewClass === 'reservation-item--be-aware' && (
                                <span className="reservation-review-label reservation-review-label--be-aware">Be Aware</span>
                              )}
                            </div>
                          )}
                          <span className="view-details">View Details &rarr;</span>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </section>
      </div>

      {renderReservationModal()}

      <MemberFormModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSaved={() => {
          if (memberId) {
            invalidateMemberDetails(parseInt(memberId, 10));
            loadMemberData(parseInt(memberId, 10));
          }
        }}
        member={member}
        corporateCode={getCorporateCode()}
      />

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        entityType="member"
        entityName={`${member.firstName} ${member.lastName}`}
        reservationCount={memberReservations.length}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setShowDeleteModal(false)}
        loading={deleteLoading}
      />

      <PassengerVehicleFormModal
        isOpen={showPVModal}
        onClose={() => setShowPVModal(false)}
        onSaved={handleVehicleSaved}
        vehicle={editingPV}
        memberId={member.id}
      />

      <RecreationalVehicleFormModal
        isOpen={showRVModal}
        onClose={() => setShowRVModal(false)}
        onSaved={handleVehicleSaved}
        vehicle={editingRV}
        memberId={member.id}
      />

      <AdminCancellationWizard
        isOpen={showCancelWizard}
        onClose={() => setShowCancelWizard(false)}
        reservation={selectedReservation}
        onSuccess={handleWizardSuccess}
      />

      <ModifyReservationWizard
        isOpen={showModifyWizard}
        onClose={() => setShowModifyWizard(false)}
        reservation={selectedReservation}
        onSuccess={handleWizardSuccess}
      />

      {showResendAgreement && selectedReservation && (
        <ResendAgreementModal
          reservation={selectedReservation}
          onClose={() => setShowResendAgreement(false)}
          onSuccess={handleWizardSuccess}
        />
      )}

      {showPaymentPopup && selectedReservation && (
        <PaymentPopup
          reservation={selectedReservation}
          onClose={() => setShowPaymentPopup(false)}
        />
      )}
    </div>
  );
};

export default MemberCard;
